package link.erpango.quip.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.VehicleColor
import link.erpango.quip.theme.VehicleColors
import link.erpango.quip.theme.VehicleColors.getStringFromKey
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.*

/**
 * Un selector de color de vehículo que muestra un botón con el color actual
 * y abre un diálogo con todos los colores disponibles.
 */
@Composable
fun VehicleColorPicker(
    selectedColorId: String,
    onColorSelected: (VehicleColor) -> Unit,
    modifier: Modifier = Modifier,
    label: String = "Color",
    isError: Boolean = false
) {
    var showDialog by remember { mutableStateOf(false) }
    val selectedColor = remember(selectedColorId) {
        VehicleColors.fromId(selectedColorId).takeIf { it.id == selectedColorId }
            ?: VehicleColors.fromName(selectedColorId)
    }
    val borderColor = when {
        isError -> Color(0xFFB00020)
        else -> Color.Gray.copy(alpha = 0.5f)
    }
    val labelColor = if (isError) Color(0xFFB00020) else QuipColors.colorTextSecondary

    Column(modifier = modifier.fillMaxWidth()) {
        Text(
            text = label,
            style = MaterialTheme.typography.caption,
            color = labelColor,
            modifier = Modifier.padding(start = 16.dp, bottom = 4.dp)
        )

        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .height(56.dp)
                .clickable { showDialog = true }
                .border(width = 1.dp, color = borderColor, shape = RoundedCornerShape(12.dp)),
            shape = RoundedCornerShape(12.dp),
            color = Color.White,
            elevation = 0.dp
        ) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 16.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    // Muestra del color
                    Box(
                        modifier = Modifier
                            .size(32.dp)
                            .clip(CircleShape)
                            .background(selectedColor.baseColor)
                            .border(
                                width = 1.dp,
                                color = if (selectedColor.baseColor.luminance() > 0.9f) 
                                    Color.Gray.copy(alpha = 0.3f) 
                                else 
                                    Color.Transparent,
                                shape = CircleShape
                            )
                    )
                    
                    Spacer(modifier = Modifier.width(12.dp))
                    Text(
                        text = if (selectedColorId.isEmpty()) { stringResource(Res.string.vehicle_color_select) }
                               else { getStringFromKey(VehicleColors.fromId(selectedColorId).nameResKey) }
                            .replace("_", " ")
                            .replaceFirstChar { it.uppercase() },
                        style = MaterialTheme.typography.body1,
                        color = QuipColors.colorText
                    )
                }
                
                // Indicador de que es clickeable
                Icon(
                    imageVector = Icons.Default.Check,
                    contentDescription = null,
                    tint = QuipColors.colorTextSecondary.copy(alpha = 0.5f),
                    modifier = Modifier.size(20.dp)
                )
            }
        }
    }
    
    if (showDialog) {
        VehicleColorPickerDialog(
            selectedColorId = selectedColor.id,
            onColorSelected = { color ->
                onColorSelected(color)
                showDialog = false
            },
            onDismiss = { showDialog = false }
        )
    }
}

/**
 * Diálogo con la cuadrícula de colores disponibles.
 */
@Composable
fun VehicleColorPickerDialog(
    selectedColorId: String,
    onColorSelected: (VehicleColor) -> Unit,
    onDismiss: () -> Unit
) {
    Dialog(onDismissRequest = onDismiss) {
        Surface(
            shape = RoundedCornerShape(16.dp),
            color = QuipColors.colorBackground,
            elevation = 8.dp
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
            ) {
                // Header
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = stringResource(Res.string.vehicle_color_select),
                        style = MaterialTheme.typography.h6,
                        fontWeight = FontWeight.Bold,
                        color = QuipColors.colorText
                    )
                    IconButton(onClick = onDismiss) {
                        Icon(
                            imageVector = Icons.Default.Close,
                            contentDescription = stringResource(Res.string.close),
                            tint = QuipColors.colorTextSecondary
                        )
                    }
                }
                
                Spacer(modifier = Modifier.height(16.dp))
                
                // Grid de colores
                LazyVerticalGrid(
                    columns = GridCells.Fixed(5),
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(400.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    items(VehicleColors.allColors) { vehicleColor ->
                        ColorCircle(
                            vehicleColor = vehicleColor,
                            isSelected = vehicleColor.id == selectedColorId,
                            onClick = { onColorSelected(vehicleColor) }
                        )
                    }
                }
            }
        }
    }
}

/**
 * Círculo individual de color en la cuadrícula.
 */
@Composable
private fun ColorCircle(
    vehicleColor: VehicleColor,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    val borderColor = when {
        isSelected -> QuipColors.colorPrimary
        vehicleColor.baseColor.luminance() > 0.9f -> Color.Gray.copy(alpha = 0.3f)
        else -> Color.Transparent
    }
    
    Box(
        modifier = Modifier
            .size(48.dp)
            .clip(CircleShape)
            .background(vehicleColor.baseColor)
            .border(
                width = if (isSelected) 3.dp else 1.dp,
                color = borderColor,
                shape = CircleShape
            )
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        if (isSelected) {
            // Checkmark
            Icon(
                imageVector = Icons.Default.Check,
                contentDescription = "Seleccionado",
                tint = if (vehicleColor.baseColor.luminance() > 0.5f) Color.Black else Color.White,
                modifier = Modifier.size(24.dp)
            )
        }
    }
}

/**
 * Extensión para calcular la luminancia de un color.
 */
private fun Color.luminance(): Float {
    return 0.299f * red + 0.587f * green + 0.114f * blue
}
